import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { getClinroUrl } from 'utils/studyConfigUtils'
import STRINGS from 'utils/strings'
import '../stylesheets/ClinroIframe.scss'

const ClinroIframe = ({ userToken, studyId, siteId }) => {
    const baseUrl = getClinroUrl()
    const iframeRef = useRef(null)
    const clinroTargetOriginRef = useRef(null)
    const [didHandshake, setDidHandshake] = useState(false)

    const clinroUrl = useMemo(() => {
        const queryParams = new URLSearchParams({
            studyId: studyId?.toString() || '',
            siteId: siteId?.toString() || '',
            isAdminPanel: 'true',
        })
        return `${baseUrl}/login?${queryParams.toString()}`
    }, [baseUrl, studyId, siteId])

    const configuredClinroOrigin = useMemo(() => {
        try {
            return new URL(baseUrl).origin
        } catch (e) {
            return null
        }
    }, [baseUrl])

    const sendAuthMessage = useCallback(
        targetOrigin => {
            if (!userToken) return
            const win = iframeRef.current?.contentWindow
            if (!win) return

            const origin =
                targetOrigin || clinroTargetOriginRef.current || configuredClinroOrigin || '*'

            try {
                win.postMessage(
                    {
                        type: 'CLINRO_ADMINPANEL_AUTH',
                        token: userToken,
                        access_token: userToken,
                        studyId: studyId?.toString() || '',
                        siteId: siteId?.toString() || '',
                        isAdminPanel: true,
                    },
                    origin,
                )
            } catch (e) {
                // Fallback when configured origin does not match iframe (e.g. CloudFront redirect).
                win.postMessage(
                    {
                        type: 'CLINRO_ADMINPANEL_AUTH',
                        token: userToken,
                        access_token: userToken,
                        studyId: studyId?.toString() || '',
                        siteId: siteId?.toString() || '',
                        isAdminPanel: true,
                    },
                    '*',
                )
            }
        },
        [userToken, studyId, siteId, configuredClinroOrigin],
    )

    useEffect(() => {
        if (!userToken) return

        const onMessage = event => {
            const data = event.data || {}

            if (data?.type === 'CLINRO_ADMINPANEL_AUTH_READY') {
                clinroTargetOriginRef.current = event.origin
                sendAuthMessage(event.origin)
                return
            }

            if (data?.type === 'CLINRO_ADMINPANEL_AUTH_ACK') {
                const expected = clinroTargetOriginRef.current
                if (expected && event.origin !== expected) return
                setDidHandshake(true)
            }
        }

        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [userToken, sendAuthMessage])

    useEffect(() => {
        if (!userToken || didHandshake) return
        let attempts = 0
        const maxAttempts = 60

        const interval = setInterval(() => {
            attempts += 1
            if (clinroTargetOriginRef.current) {
                sendAuthMessage(clinroTargetOriginRef.current)
            }
            if (attempts >= maxAttempts) clearInterval(interval)
        }, 500)

        return () => clearInterval(interval)
    }, [userToken, didHandshake, sendAuthMessage])

    return (
        <div className='clinro-iframe page'>
            <div className='clinro-header'>
                <h4>{STRINGS.clinro}</h4>
            </div>
            <iframe
                className='clinro-embedded-iframe'
                src={clinroUrl}
                id='clinro-iframe'
                title='ClinRO Application'
                allowFullScreen
                ref={iframeRef}
                onLoad={() => {
                    setDidHandshake(false)
                    clinroTargetOriginRef.current = null
                }}
                referrerPolicy='origin'
            />
        </div>
    )
}

ClinroIframe.propTypes = {
    userToken: PropTypes.string.isRequired,
    studyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default ClinroIframe
